// AGC: Base App utility helpers (formatting, metadata, context detection)

import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * AGC: Truncate Ethereum addresses for UI display.
 */
export function truncateAddress(address: string | undefined, prefixLength = 6, suffixLength = 4): string {
  if (!address) {
    return ''
  }
  if (address.length <= prefixLength + suffixLength) {
    return address
  }
  return `${address.slice(0, prefixLength)}...${address.slice(-suffixLength)}`
}

/**
 * AGC: Normalize OGB balance output (handles null/undefined/NaN).
 */
export function formatOgbBalanceDisplay(balance: number | string | undefined | null): string {
  if (balance === undefined || balance === null || balance === '' || Number.isNaN(Number(balance))) {
    return '0'
  }

  const numericValue = typeof balance === 'string' ? parseFloat(balance) : balance
  if (Number.isNaN(numericValue) || !Number.isFinite(numericValue)) {
    return '0'
  }

  return Math.floor(numericValue).toLocaleString('en-US')
}

/**
 * AGC: Merge Tailwind class names safely.
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * AGC: Manifest / connector metadata shared across Base App helpers.
 */
export const METADATA = {
  name: 'Origami Battles',
  description: 'A strategic battle game on Base blockchain with Farcaster integration',
  url: 'https://origamibattle.app',
  icon: 'https://origamibattle.app/logo.png',
}

/**
 * AGC: Detect if the bundle is running in Base App (Farcaster) context.
 */
export function isBaseAppContext(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  const appContext = import.meta.env.VITE_APP_CONTEXT
  return appContext === 'baseapp' || appContext === 'farcaster'
}

/**
 * AGC: Friendly timestamp formatter (accepts seconds or milliseconds).
 */
export function formatTimestamp(timestamp: number): string {
  const date = timestamp > 1_000_000_000_0 ? new Date(timestamp) : new Date(timestamp * 1000)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * AGC: Safe number parsing with configurable fallback.
 */
export function parseNumberSafe(value: unknown, fallback: number = 0): number {
  const parsed = Number(value)
  return Number.isNaN(parsed) || !Number.isFinite(parsed) ? fallback : parsed
}

